import documentVersionsService from '../service/documentVersions.service';
import { EventBus, DOCUMENT_EVENTS } from '@/event-bus';
import { $handleError } from '@/utils';
import { error as debugError } from '@/utils/debugLogger';
import Vue from 'vue';
import {
  toNameItems,
  conflictsOf,
  applyNameChoices,
  API_NEW_VERSION,
  duplicateIdsFrom,
  versionUploadOutcomes,
  versionOutcomeMessages,
} from './documentNameConflicts';

// A rename can land on another taken name; ask again, but not forever.
const MAX_ROUNDS = 5;

function askUser(conflicts, drafts, scopeLabel, fileOf) {
  const listeners = EventBus._events?.[DOCUMENT_EVENTS.RESOLVE_NAME_CONFLICTS];
  if (!listeners || listeners.length === 0) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    EventBus.$emit(DOCUMENT_EVENTS.RESOLVE_NAME_CONFLICTS, { conflicts, drafts, scopeLabel, fileOf, resolve });
  });
}

/**
 * Before documents are created in a facility (or facility group), finds the drafts whose number
 * or title is already taken there, or by another draft, and asks the user per file: add it as a
 * new version of the existing document, keep both under another name, or skip it.
 *
 * Drafts are the objects later sent to POST api/documents/AddDocuments (at least documentNumber,
 * documentTitle, fileName). Returns the drafts to send, in order: unchanged, renamed, or marked
 * `onNameConflict: 'NewVersion'` with `targetDocumentId`; skipped drafts are left out. Returns
 * null when the user cancels the upload.
 *
 * When the check cannot run (no prompt mounted, request failed) the drafts are returned as they
 * are and the API still refuses a taken name, as before.
 *
 * With `options.fileOf`, the prompt also compares each file offered as a new version with the
 * existing document's current version, and flags the ones with identical content before upload.
 *
 * @param {Array} drafts
 * @param {{ assetId?: string|null, facilityGroupId?: string|null }} scope
 * @param {{ fileOf?: (draft: Object) => (File|undefined) }} [options]
 */
export async function resolveDocumentNameConflicts(drafts, scope, options = {}) {
  if (!drafts?.length) return drafts || [];
  const assetId = scope?.facilityGroupId ? null : scope?.assetId || null;
  const facilityGroupId = scope?.facilityGroupId || null;
  if (!assetId && !facilityGroupId) return drafts;
  const scopeLabel = facilityGroupId ? 'this facility group' : 'this facility';

  let current = drafts;
  for (let round = 0; round < MAX_ROUNDS; round++) {
    // Drafts already marked as a new version conflict on purpose; check the rest.
    const pending = current.map((d, i) => ({ d, i })).filter(({ d }) => d.onNameConflict !== API_NEW_VERSION);
    if (pending.length === 0) return current;

    let results;
    try {
      const res = await documentVersionsService.checkNames({
        assetId,
        facilityGroupId,
        items: toNameItems(pending.map((p) => p.d)),
      });
      results = res.data || [];
    } catch (err) {
      debugError('[NameConflicts]', 'check-names failed:', err);
      $handleError(err);
      return current;
    }

    // Results are keyed by position in `pending`; map them back to positions in `current`.
    const conflicts = conflictsOf(results).map((c) => ({ ...c, index: pending[c.index].i }));
    if (conflicts.length === 0) return current;

    const choices = await askUser(conflicts, current, scopeLabel, options.fileOf);
    if (choices === undefined) return current; // nobody to ask: let the API decide
    if (choices === null) return null; // cancelled
    current = applyNameChoices(current, choices, conflicts);
  }
  return current;
}

/**
 * After POST AddDocuments: tells the user what happened to each file sent as a new version
 * (now vN / added but a pinned version stays current / identical to the current version, so
 * nothing added), and announces the changed documents on DOCUMENT_CHANGED.
 *
 * @param {Array} drafts the documents sent, in order
 * @param {Array} documents the DocumentVMs returned, in the same order
 * @param {Object} headers the response headers
 */
export function reportVersionUploads(drafts, documents, headers) {
  const outcomes = versionUploadOutcomes(drafts, documents, duplicateIdsFrom(headers));
  versionOutcomeMessages(outcomes).forEach(({ type, text }) => {
    Vue.prototype.$dialog?.notify?.[type]?.(text, { position: 'top-right', timeout: 6000 });
  });
  outcomes
    .filter((o) => o.outcome !== 'duplicate')
    .forEach((o) => {
      const document = documents.find((d) => d.id === o.documentId);
      if (document) EventBus.$emit(DOCUMENT_EVENTS.DOCUMENT_CHANGED, document);
    });
  return outcomes;
}
